import type { WorkClaimDto, WorkClaimRef, WorkFamily } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Button, ErrorText } from '../../ui';

/** Un esecutore alla volta; l'attesa sui dati riguarda lo stesso meccanismo per persone e sistemi. */
export function WorkClaimControls({ family, taskId, canClaim, onCanWorkChange }: { family: WorkFamily; taskId: string; canClaim: boolean; onCanWorkChange: (value: boolean) => void }) {
  const { tenant, t, isManager } = useTenant();
  const qc = useQueryClient();
  const path = `/work-claims/${family}/${taskId}`;
  const query = useTenantQuery<{ claim: WorkClaimDto | null }>(['work-claim', family, taskId], path, { refetchInterval: 10_000 });
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['tenant', tenant.id, 'work-claim', family, taskId] }); };
  const update = (claim: WorkClaimDto) => { qc.setQueryData(['tenant', tenant.id, 'work-claim', family, taskId], { claim }); refresh(); };
  const take = useMutation({ mutationFn: () => api<WorkClaimDto>(t(`${path}/claim`), { body: {} }), onSuccess: update });
  const release = useMutation({ mutationFn: (body: WorkClaimRef | { force: true }) => api<WorkClaimDto>(t(`${path}/release`), { body }), onSuccess: update });
  const renew = useMutation({ mutationFn: (body: WorkClaimRef) => api<WorkClaimDto>(t(`${path}/renew`), { body }), onSuccess: update, onError: refresh });
  const claim = query.data?.claim ?? null;
  const alive = !!claim && (claim.state === 'active' || claim.state === 'waiting') && new Date(claim.expires_at).getTime() > Date.now();
  const mine = alive && claim.token !== null;
  const waiting = alive && claim.state === 'waiting';
  const busy = take.isPending || release.isPending;
  const available = canClaim && !query.isError && !query.isLoading && (!alive || (mine && !waiting));
  useEffect(() => onCanWorkChange(available), [available, onCanWorkChange]);

  const renewClaim = renew.mutate;
  const id = claim?.id;
  const token = claim?.token;
  const fence = claim?.fence;
  useEffect(() => {
    if (!mine || !id || !token || !fence) return;
    const timer = window.setInterval(() => renewClaim({ id, token, fence }), 60_000);
    return () => window.clearInterval(timer);
  }, [mine, id, token, fence, renewClaim]);

  const error = take.error ?? release.error ?? renew.error ?? query.error;
  return (
    <div className="mt-3 rounded-lg bg-black/[0.025] px-3 py-2 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        {alive ? (
          <span className={waiting ? 'text-amber-800' : 'text-ink/70'}>
            {mine ? waiting ? 'Il tuo compito è in coda: il dato è impegnato da un altro lavoro.' : 'Preso in carico da te.' : `${waiting ? 'In coda' : 'In carico'} a ${claim.owner.name}.`}
          </span>
        ) : <span className="text-ink/60">{claim?.state === 'active' || claim?.state === 'waiting' ? 'La presa in carico è scaduta.' : 'Disponibile per la presa in carico.'}</span>}
        {canClaim && !alive && <Button variant="ghost" className="px-2 py-1 text-xs" disabled={busy || query.isLoading} onClick={() => take.mutate()}>Prendi in carico</Button>}
        {mine && <Button variant="ghost" className="px-2 py-1 text-xs" disabled={busy} onClick={() => release.mutate({ id: claim.id, token: claim.token!, fence: claim.fence })}>Rilascia</Button>}
        {isManager && alive && !mine && <Button variant="ghost" className="px-2 py-1 text-xs" disabled={busy} onClick={() => release.mutate({ force: true })}>Forza rilascio</Button>}
      </div>
      {mine && claim.resources.length === 0 && <p className="mt-1 text-ink/50">Nessun dato collegato: la presa in carico protegge questo compito.</p>}
      {error && <ErrorText>{errorMessage(error)}</ErrorText>}
    </div>
  );
}
